export type ToolCallStatus = "running" | "complete" | "error";

export interface StreamTool {
  key: string;
  name: string;
  status: ToolCallStatus;
  detail?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(data: unknown, keys: string[]): string | undefined {
  if (!isRecord(data)) return undefined;
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return undefined;
}

function formatUnknown(value: unknown): string | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function summarizeToolPayload(
  data: unknown,
  keys: string[],
): string | undefined {
  if (!isRecord(data)) return formatUnknown(data);
  for (const key of keys) {
    if (key in data) return formatUnknown(data[key]);
  }
  return undefined;
}

let toolCallSequence = 0;
export function normalizeToolCall(data: unknown): StreamTool[] {
  if (typeof data === "string" && data.trim()) {
    return data
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((name) => ({
        key: `${name}:${toolCallSequence++}`,
        name,
        status: "running" as const,
      }));
  }

  const name =
    readString(data, ["tool_name", "tool", "name", "function_name"]) ?? "";
  const detail =
    readString(data, ["description", "summary", "message"]) ??
    summarizeToolPayload(data, [
      "arguments",
      "args",
      "input",
      "parameters",
      "query",
    ]);

  return [
    {
      key: name,
      name,
      status: "running",
      detail,
    },
  ];
}

function updateToolAt(
  tools: StreamTool[],
  index: number,
  patch: Partial<StreamTool>,
): StreamTool[] {
  const next = tools.slice();
  next[index] = { ...next[index], ...patch };
  return next;
}

/**
 * Upsert a tool-call entry by key: update in place if it exists, else append.
 * A new tool-call means the previous one(s) are done, so any still-"running"
 * tools are marked "complete" before the new entry is appended.
 */
export function mergeToolCall(
  tools: StreamTool[],
  next: StreamTool,
): StreamTool[] {
  const index = tools.findIndex((tool) => tool.key === next.key);
  if (index === -1) return [...completeRunningTools(tools), next];
  return updateToolAt(tools, index, next);
}

/** Mark any still-"running" tools as "complete" (e.g. once the stream is done). */
export function completeRunningTools(tools: StreamTool[]): StreamTool[] {
  if (!tools.some((tool) => tool.status === "running")) return tools;
  return tools.map((tool) =>
    tool.status === "running" ? { ...tool, status: "complete" } : tool,
  );
}

/** Normalize a persisted message's tool_calls field for non-streaming display. */
export function normalizeHistoricalToolCalls(data: unknown): StreamTool[] {
  if (!Array.isArray(data)) return [];

  return data.map((call, index) => {
    if (typeof call === "string" && call.trim()) {
      const name = call.trim();
      return { key: `${name}:${index}`, name, status: "complete" as const };
    }

    const name =
      readString(call, ["tool_name", "tool", "name", "function_name"]) ?? "";
    const detail =
      readString(call, ["text", "description", "summary", "message"]) ??
      summarizeToolPayload(call, [
        "arguments",
        "args",
        "input",
        "parameters",
        "query",
      ]);

    return {
      key: `${name}:${index}`,
      name,
      status: "complete" as const,
      detail,
    };
  });
}
